"use client";

/**
 * PreviewCarousel — multi-frame artifact peek for Scene 4 day-beat blocks.
 *
 * Replaces the single-image PreviewFrame when a block needs depth. Shell,
 * shadow, border, and rotate-on-hover styling match PreviewFrame so the
 * visual vocabulary stays consistent — only the content layer rotates.
 *
 * Behavior:
 *   - all frames mount up-front and stack absolutely; current frame has
 *     opacity 1, others 0 (smooth crossfade, no AnimatePresence brittleness)
 *   - auto-advance every AUTO_ADVANCE_MS while idle (not hovered, not focused,
 *     not actively dragging)
 *   - dots indicator: real buttons, click jumps to frame, keyboard works
 *   - touch + mouse swipe (left/right) advances
 *   - reduced-motion: shows only frame 0, no auto-advance, no rotate
 *
 * Hover/pause split:
 *   - rotation animation lives on the OUTER motion.div (framer-motion hooks)
 *   - pause + swipe handlers live on an INNER div using plain DOM events,
 *     so framer's pointer interception cannot eat them
 */

import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { PreviewImage } from "../data/previewImages";

const AUTO_ADVANCE_MS = 3500;
const SWIPE_THRESHOLD_PX = 40;

interface PreviewCarouselProps {
  /** 1..N preview frames. Order = display order = story arc. */
  frames: readonly PreviewImage[];
  /** Idle rotation (degrees). Hover/focus eases toward rotateDeg/2. */
  rotateDeg: number;
  /** Optional context label for the region (a11y) — e.g. "Plan tour". */
  ariaLabel?: string;
}

export function PreviewCarousel({
  frames,
  rotateDeg,
  ariaLabel,
}: PreviewCarouselProps) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragStartXRef = useRef<number | null>(null);

  const goTo = useCallback(
    (i: number) => setIndex(((i % frames.length) + frames.length) % frames.length),
    [frames.length],
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-advance — disabled when reduced-motion, paused, or single-frame.
  useEffect(() => {
    if (reduced || paused || frames.length < 2) return;
    const t = window.setTimeout(() => {
      setIndex((i) => (i + 1) % frames.length);
    }, AUTO_ADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [index, paused, reduced, frames.length]);

  // Pointer drag (covers both mouse and touch via pointer events).
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = e.clientX;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const startX = dragStartXRef.current;
    dragStartXRef.current = null;
    if (startX == null || frames.length < 2) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (dx < 0) next();
    else prev();
  };
  const onPointerCancel = () => {
    dragStartXRef.current = null;
  };

  const showDots = !reduced && frames.length >= 2;

  return (
    <motion.div
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel ?? frames[0]?.alt}
      tabIndex={0}
      initial={reduced ? false : { rotate: rotateDeg }}
      whileHover={
        reduced ? undefined : { rotate: rotateDeg / 2, scale: 1.02 }
      }
      whileFocus={
        reduced ? undefined : { rotate: rotateDeg / 2, scale: 1.02 }
      }
      transition={{ type: "spring", stiffness: 220, damping: 20 }}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-white/10",
        "ring-1 ring-inset ring-white/5",
        "shadow-[0_20px_40px_-15px_rgba(251,191,36,0.25)]",
        "hover:border-amber-400/20 hover:shadow-[0_28px_50px_-15px_rgba(251,191,36,0.35)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
        "transition-shadow duration-300",
      )}
    >
      {/* Inner pause/swipe surface — plain DOM events, not framer's hooks,
          so framer's `whileHover`/`whileFocus` on the parent cannot eat them. */}
      <div
        className="relative aspect-[16/10] w-full touch-pan-y"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {frames.map((f, i) => {
          const active = i === index;
          return (
            <div
              key={f.src}
              aria-hidden={!active}
              className={cn(
                "absolute inset-0 transition-opacity duration-500 ease-out",
                active ? "opacity-100" : "opacity-0",
              )}
            >
              <Image
                src={f.src}
                alt={f.alt}
                width={f.width}
                height={f.height}
                sizes="(max-width: 768px) 100vw, 60vw"
                className="h-full w-full select-none object-cover"
                priority={i === 0}
                loading={i === 0 ? "eager" : "lazy"}
              />
            </div>
          );
        })}

        {/* Subtle inner vignette so cropped screenshots blend into surface. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_55%,_rgba(0,0,0,0.25)_100%)]"
        />

        {/* Dots indicator. */}
        {showDots && (
          <div
            role="tablist"
            aria-label={`${ariaLabel ?? "Preview"} frames`}
            className={cn(
              "absolute bottom-3 left-1/2 z-20 -translate-x-1/2",
              "flex items-center gap-1.5",
              "rounded-full border border-white/10 bg-black/45 px-2.5 py-1.5 backdrop-blur-sm",
            )}
          >
            {frames.map((f, i) => {
              const active = i === index;
              return (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-current={active ? "true" : undefined}
                  aria-label={`Frame ${i + 1} of ${frames.length}: ${f.alt}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    goTo(i);
                  }}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40",
                    active
                      ? "w-5 bg-amber-300"
                      : "w-1.5 bg-white/40 hover:bg-white/70",
                  )}
                />
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}
